// v4 gold-rock — REDESIGNED. A SPLIT HOST STONE — a dark boulder cracked
// into two leaning lobes, the fracture between them filled with a zig-zag
// seam of bright gold wedges that catches the eye from far off, plus a couple
// of spilled nuggets and warm-flecked scree at the foot. Each variant
// re-opens the crack at a different angle.
//
// Ported 1:1 from age-of-mcp's v4/gold-rock.ts. The v4 `scree` helper (with
// its `h01`/`lerp`) is inlined locally because v4's variant differs from the
// library's `mineral.scree`.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function scree(
  seed: number, count: number,
  dist: readonly [number, number], size: readonly [number, number],
  colors: readonly string[],
): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + h01(seed, i * 4) * 1.6;
    const d = lerp(dist[0], dist[1], h01(seed, i * 4 + 1));
    const r = lerp(size[0], size[1], h01(seed, i * 4 + 2));
    const shard = i % 2 === 1;
    parts.push({
      geometry: shard
        ? new THREE.TetrahedronGeometry(r * 0.95, 0)
        : new THREE.IcosahedronGeometry(r, 0),
      color: colors[i % colors.length]!,
      position: [Math.cos(ang) * d, shard ? r * 0.28 : r * 0.5, Math.sin(ang) * d],
      rotation: [
        h01(seed, i * 4 + 3) * 0.9,
        h01(seed, i * 7 + 11) * Math.PI * 2,
        h01(seed, i * 5 + 23) * 0.9,
      ],
      scale: [1.05, 0.92, 1.0],
    });
  }
  return parts;
}

const HOST   = "#4f4b44";   // dark umber host rock
const HOST2  = "#5c5850";
const GOLD   = "#f2c43c";   // bright seam gold
const GOLD2  = "#ffd95a";   // glint
const GOLD3  = "#d9a52a";   // shaded gold

function partsFor(v: number): Part[] {
  const seed = v * 13 + 7;
  const yaw  = v * 2.3;
  const gap  = lerp(0.1, 0.15, h01(seed, 0));   // how far the crack opens
  const parts: Part[] = [
    // the two split lobes, leaning apart around the gold seam
    { geometry: new THREE.IcosahedronGeometry(0.21, 0), color: HOST,
      position: [-gap, 0.18, 0],
      rotation: [0.1, yaw, 0.3],
      scale: [1.0, 1.25, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.18, 0), color: HOST2,
      position: [gap + 0.02, 0.15, 0.02],
      rotation: [-0.08, yaw + 0.9, -0.34],
      scale: [0.95, 1.2, 1.0] },
  ];

  // The exposed seam: a zig-zag of gold wedges climbing the fracture.
  const steps = 4;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    parts.push({
      geometry: new THREE.TetrahedronGeometry(lerp(0.055, 0.085, h01(seed, 10 + i)), 0),
      color: [GOLD, GOLD2, GOLD3][i % 3]!,
      position: [
        lerp(-0.025, 0.03, t) + (i % 2 === 0 ? -0.015 : 0.02),
        lerp(0.06, 0.3, t),
        lerp(0.03, -0.04, t),
      ],
      rotation: [h01(seed, 20 + i) * 1.2, yaw + i * 1.1, h01(seed, 30 + i) * 1.2],
      scale: [1.0, 1.35, 0.9],
    });
  }

  // Two spilled nuggets where the seam weathered out.
  for (let i = 0; i < 2; i++) {
    const ang = yaw + 0.8 + i * 2.6;
    parts.push({
      geometry: new THREE.IcosahedronGeometry(lerp(0.045, 0.06, h01(seed, 40 + i)), 0),
      color: i === 0 ? GOLD2 : GOLD3,
      position: [Math.cos(ang) * 0.1, 0.045, Math.sin(ang) * 0.1],
      rotation: [0, ang, 0.3],
      scale: [1.1, 0.75, 1.0],
    });
  }

  parts.push(...scree(seed, 3, [0.1, 0.15], [0.03, 0.05], [HOST, HOST2, HOST]));
  return parts;
}

export const goldRockV4 = defineModel({
  id: "gold-rock-v4",
  batchId: "polished",
  name: "Gold rock (v4)",
  tags: ["scenery","gold","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly gold rock.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.42,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
    { id: "variant-2", label: "Variant 3", parts: () => partsFor(2) },
  ],
  animations: [],
});
